import { useEffect } from "react";

/** Ред от детайла на масата, по който се ходи със стрелките. */
export interface KeyboardOrderItem {
  /** id на реда в сметката (bill_element_items.id) */
  id: number;
  /** id на продукта — за повторната поръчка с „+" */
  productId: number;
  /** Обедното меню на реда (null = обикновен) — повторната поръчка е от него */
  lunchMenuId?: number | null;
}

interface OrderItemsKeyboardOptions {
  /** Десктоп, изглед „маси", без отворен модал/popover. */
  enabled: boolean;
  /** Редовете в реда на показване (необобщен изглед). */
  items: KeyboardOrderItem[];
  /** Избраният ред; стрелките тук работят САМО докато той е в `items` —
   *  иначе фокусът е в списъка с маси (TableList). */
  selectedItemId: number | null;
  onSelect: (itemId: number | null) => void;
  /** „+" — отваря „Промени" за продукта, както двойният клик на реда. */
  onReorder: (productId: number, lunchMenuId?: number | null) => void;
}

/**
 * Клавиатурата в дясната секция на масите (десктоп): ↑ ↓ ходят по поръчаните
 * редове, ← (и Esc) връща фокуса в списъка с маси, „+" отваря „Промени" за
 * избрания ред. Влизането тук е с → от десния край на реда с маси
 * (TableList → onArrowRightEdge). Игнорира се при фокус в поле за писане.
 */
export function useOrderItemsKeyboard({
  enabled,
  items,
  selectedItemId,
  onSelect,
  onReorder,
}: OrderItemsKeyboardOptions) {
  useEffect(() => {
    if (!enabled) return;
    const currentIndex = items.findIndex((item) => item.id === selectedItemId);
    if (currentIndex === -1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      )
        return;

      // „+" по физическия клавиш: „=/+" (със или без Shift, при всяка
      // подредба — БДС/фонетична дават друг символ) и „+" от цифровия блок.
      // С Cmd/Ctrl/Alt е клавишна комбинация (Cmd/Ctrl + „=" = zoom) — не пипаме.
      const isPlusKey =
        event.key === "+" ||
        event.code === "Equal" ||
        event.code === "NumpadAdd";
      if (isPlusKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        onReorder(items[currentIndex].productId, items[currentIndex].lunchMenuId);
        return;
      }

      switch (event.key) {
        case "ArrowUp":
        case "ArrowDown": {
          event.preventDefault();
          const nextIndex =
            event.key === "ArrowUp"
              ? Math.max(0, currentIndex - 1)
              : Math.min(items.length - 1, currentIndex + 1);
          if (nextIndex !== currentIndex) onSelect(items[nextIndex].id);
          break;
        }
        case "ArrowLeft":
        case "Escape":
          // preventDefault = „Esc е консумиран" — само излиза от редовете,
          // без глобалната Esc-навигация „назад" (SharedAccountGate).
          event.preventDefault();
          onSelect(null);
          break;
        case "ArrowRight":
          // Дясната секция е последната — не скролира страницата.
          event.preventDefault();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, items, selectedItemId, onSelect, onReorder]);
}
